/** Maps machine master records between API snake_case/camelCase and form camelCase. */

const CAMEL_TO_SNAKE: Record<string, string> = {
  companyName: 'company_name',
  companyId: 'company_id',
  branchName: 'branch_name',
  branchId: 'branch_id',
  projectType: 'project_type',
  projectId: 'project_id',
  projectName: 'project_name',
  projectLocation: 'project_location',
  machineId: 'machine_id',
  machineName: 'machine_name',
  machineType: 'machine_type',
  machineCategory: 'machine_category',
  serialNumber: 'serial_number',
  manufacturer: 'manufacturer',
  model: 'model',
  yearOfManufacture: 'year_of_manufacture',
  purchaseDate: 'purchase_date',
  acquisitionCost: 'acquisition_cost',
  currentValue: 'current_value',
  ownerType: 'owner_type',
  ownerName: 'owner_name',
  ownerContact: 'owner_contact',
  ownerAddress: 'owner_address',
  machineOwnerId: 'machine_owner_id',
  leaseStatus: 'lease_status',
  leasingCompany: 'leasing_company',
  leaseStartDate: 'lease_start_date',
  leaseEndDate: 'lease_end_date',
  leaseTerms: 'lease_terms',
  currentLocation: 'current_location',
  department: 'department',
  operationalStatus: 'operational_status',
  lastMaintenanceDate: 'last_maintenance_date',
  nextMaintenanceDate: 'next_maintenance_date',
  maintenanceHistory: 'maintenance_history',
  usageHours: 'usage_hours',
  fuelType: 'fuel_type',
  fuelConsumptionRate: 'fuel_consumption_rate',
  operatorName: 'operator_name',
  operatorLicense: 'operator_license',
  condition: 'machine_condition',
  warrantyStatus: 'warranty_status',
  warrantyExpiryDate: 'warranty_expiry_date',
  insuranceStatus: 'insurance_status',
  insuranceProvider: 'insurance_provider',
  insurancePolicyNumber: 'insurance_policy_number',
  depreciationRate: 'depreciation_rate',
  sigmaAssetTag: 'sigma_asset_tag',
  qrCodeId: 'qr_code_id',
  lastUpdated: 'last_updated',
  notes: 'notes',
};

const DATE_FIELDS = new Set([
  'purchaseDate',
  'leaseStartDate',
  'leaseEndDate',
  'lastMaintenanceDate',
  'nextMaintenanceDate',
  'warrantyExpiryDate',
  'lastUpdated',
]);

function readValue(raw: Record<string, unknown>, camel: string, snake: string): unknown {
  if (raw[camel] !== undefined && raw[camel] !== null && raw[camel] !== '') {
    return raw[camel];
  }
  if (raw[snake] !== undefined && raw[snake] !== null && raw[snake] !== '') {
    return raw[snake];
  }
  if (camel === 'condition') {
    const alt = raw['machineCondition'];
    if (alt !== undefined && alt !== null && alt !== '') return alt;
  }
  return undefined;
}

function toDateInput(value: unknown): unknown {
  if (value == null || value === '') return value;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return value;
}

/** Normalize GET /machine-master/:id payload for reactive form patchValue. */
export function machineMasterToForm(raw: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!raw) {
    return {};
  }

  const form: Record<string, unknown> = {};

  for (const [camel, snake] of Object.entries(CAMEL_TO_SNAKE)) {
    const value = readValue(raw, camel, snake);
    if (value !== undefined) {
      form[camel] = DATE_FIELDS.has(camel) ? toDateInput(value) : value;
    }
  }

  return form;
}

/** Build POST body: camelCase for the API plus snake_case for import-style consumers. */
export function machineFormToApi(form: Record<string, unknown>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const [camel, snake] of Object.entries(CAMEL_TO_SNAKE)) {
    const value = form[camel];
    if (value !== undefined && value !== null && value !== '') {
      payload[camel] = value;
      payload[snake] = value;
    }
  }

  return payload;
}

/** ISO date (yyyy-MM-dd) for last_updated field. */
export function machineLastUpdatedValue(): string {
  return new Date().toISOString().slice(0, 10);
}
